import {Injectable}                       from '@angular/core';
import {
  ApDynGridColumnConfigBase
}                                                       from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                       from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridStatusSwitchColumnConfig,
  IStateChange
}                                                       from '../../ap-dyngrids/config/ap-dyn-grid-status-switch-column.config';
import {ApUtilService}                                  from '../../ap-utils/service/ap-util.service';
import {
  ApDynGridPropertyColumnConfig,
  FilterType,
  PropertyTemplate
}                                                       from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridGroupColumnConfig
}                                                       from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  ApDynGridEditColumnConfig
}                                                       from '../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {GetRoundNumericPipe}                            from '../../ap-utils';
import {Subscription}                                   from 'rxjs';
import {LanguageStore}                                  from '../../stores/translation/language.store';
import {SettingsStore}                                  from '../../stores/base-data/settings.store';
import {PpLogfileService}                               from '../services/pp-logfile.service';
import {nameOf}                                         from '../../ap-core/utils/ap-name-of';
import {IPpLogfileGridItem, IPpLogfileGridItemPlanning} from '../types/pp-logfile.types';
import {ApTranslationPipe}                              from '../../translation/pipes/ap-translation.pipe';
import IDriver = Data.DocuContext.Driver.IDriver;

@Injectable({
  providedIn: 'root'
})
export class PpLogfilesGridConfig {
  public constructor(private languageStore: LanguageStore,
                     private roundNumericPipe: GetRoundNumericPipe,
                     private service: PpLogfileService,
                     private settingStore: SettingsStore,
                     private translationPipe: ApTranslationPipe) {
  }

  public getGridColumns(subscriptions: Subscription[]): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: nameOf<IPpLogfileGridItem>('Status'),
        class: nameOf<IPpLogfileGridItem>('StatusClass'),
        description: nameOf<IPpLogfileGridItem>('StatusDesc'),
        groupDesc: true
      }),
      new ApDynGridStatusSwitchColumnConfig({
        field: nameOf<IPpLogfileGridItem>('LogStatus'),
        readonly: true,
        filterable: true,
        defaultFilter: 0,
        disableGreen: (item: IPpLogfileGridItem) => {
          if (item == null || item.SourceItem == null || item.Planning == null) {
            return false;
          }
          item.SourceItem.PlantProtectionPlanBook = item.Planning.SourceItem;
          return !this.service.logFileValid(item.SourceItem) || !this.service.logBookingReady(item.SourceItem);
        },
        stateChange: ApUtilService.createEventEmitter(
          (event: IStateChange) => {
            if (event.state === -1) {
              this.service.onReject(event.item);
            } else if (event.state === 0) {
              this.service.onReset(event.item);
            } else if (event.state === 1) {
              this.service.onAccept(event.item);
            }
          }, subscriptions),
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__FieldName',
        field: nameOf<IPpLogfileGridItem>('FieldName'),
        dynCssClass: (item) => item[nameOf<IPpLogfileGridItem>('FieldName')] === 'Global__UnknownFieldName' ? ' italic' : '',
        width: 180,
        headerFilterable: true,
        filterable: true,
        pipes: [
          {
            pipe: this.translationPipe,
            args: []
          }
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global_ha_Unit',
        field: nameOf<IPpLogfileGridItem>('FieldArea'),
        width: 60,
        headerFilterable: true,
        cssClass: 'right',
        dynCssClass: (item) => item[nameOf<IPpLogfileGridItem>('FieldName')] === 'Global__UnknownFieldName' ? ' italic' : '',
        filterType: FilterType.NUMBER,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'N__Logfile',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Filename',
            field: nameOf<IPpLogfileGridItem>('FileName'),
            width: 100,
            filterable: true,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Date',
            field: nameOf<IPpLogfileGridItem>('Date'),
            format: this.languageStore.SelectedLanguage.DateFormat,
            width: 80,
            filterable: true,
            headerFilterable: true,
            sortIndex: 0,
            sortDesc: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Crop',
            field: nameOf<IPpLogfileGridItem>('Crop'),
            width: 100,
            headerFilterable: true,
            filterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__ECStageShort',
            field: nameOf<IPpLogfileGridItem>('Ec'),
            width: 40,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            filterable: true,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Products',
            field: `${nameOf<IPpLogfileGridItem>('Products')}`,
            width: 100,
            headerFilterable: true,
            filterable: true,
            multiline: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Avg_Water',
            field: `${nameOf<IPpLogfileGridItem>('Water')}`,
            headerExtraLines: ['Unit_l_per_ha'],
            width: 70,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            headerFilterable: true,
            filterable: true,
            hide: true,
            tooltip: (dataItem: IPpLogfileGridItem) => this.roundNumericPipe.transform(dataItem.Water),
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          })
        ]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Planning',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Note',
            field: `${nameOf<IPpLogfileGridItem>('Planning')}.${nameOf<IPpLogfileGridItemPlanning>('Note')}`,
            width: 100,
            headerFilterable: true,
            filterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Date',
            field: `${nameOf<IPpLogfileGridItem>('Planning')}.${nameOf<IPpLogfileGridItemPlanning>('Date')}`,
            format: this.languageStore.SelectedLanguage.DateFormat,
            width: 80,
            headerFilterable: true,
            filterable: true,
            filterType: FilterType.DATE,
            template: PropertyTemplate.DATE
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Docu_Ini__Motorist',
            field: `${nameOf<IPpLogfileGridItem>('Planning')}.${nameOf<IPpLogfileGridItemPlanning>('Driver')}.${nameOf<IDriver>('ShortFullName')}`,
            width: 100,
            headerFilterable: true,
            filterable: true
          }),
        ]
      }),
      new ApDynGridEditColumnConfig({
        data: [
          {
            text: 'Global__Edit',
            callback: ApUtilService.createEventEmitter(this.service.onEdit.bind(this.service), subscriptions),
            //                                                 is red                is not booked
            disabled: (item: IPpLogfileGridItem) => item.Status === -1 || item.LogStatus !== 0
          },
          {
            text: 'Global__Booking',
            callback: ApUtilService.createEventEmitter(this.service.onAccept.bind(this.service), subscriptions),
            disabled: (item: IPpLogfileGridItem) => {
              if (item == null || item.SourceItem == null || item.Planning == null) {
                return false;
              }
              item.SourceItem.PlantProtectionPlanBook = item.Planning.SourceItem;
              return !this.service.logFileValid(item.SourceItem) || !this.service.logBookingReady(item.SourceItem) || this.service.logBooked(item.SourceItem);
            }
          },
          {
            text: 'Global__Reject',
            callback: ApUtilService.createEventEmitter(this.service.onReject.bind(this.service), subscriptions),
          },
          {
            text: 'Global__Logfile_Remap',
            callback: ApUtilService.createEventEmitter(this.service.onRemap.bind(this.service), subscriptions),
            disabled: (item: IPpLogfileGridItem) => item.LogStatus === 1
          }
        ]
      })
    ];
  }
}
